import { action } from "@storybook/addon-actions";
import type { Meta, StoryFn } from "@storybook/react";
import { SearchIcon } from "lucide-react";
import React, { useState } from "react";
import { useAsyncList } from "react-stately";
import { Autocomplete, AutocompleteItem } from ".";
import Flex from "../../Flex";
import { Typography } from "../../Typography";

export default {
    component: Autocomplete,
    parameters: {
        controls: {
            sort: "alpha"
        }
    },
    title: "Form components/Autocomplete"
} as Meta<typeof Autocomplete>;

const items = [
    { id: 1, name: "Red panda" },
    { id: 2, name: "Cat" },
    { id: 3, name: "Dog" },
    { id: 4, name: "Aardvark" },
    { id: 5, name: "Kangaroo" },
    { id: 6, name: "Snake" }
];

/**
 * This example showcases the autocomplete functionalities with as little customizations as possible.
 * Please note that **options aren't styled by default**; we're applying some default styling here, that you can see by clicking the "Show code" button.
 *
 * _Ensuring that the options are tall enough and spaced enough to be clickable also on mobile devices is your responsibility when using this component._
 */
export const Unstyled: StoryFn = args => (
    <Flex column maxWidth={300}>
        <Autocomplete items={items} menuTrigger="focus" {...args}>
            {item => (
                <AutocompleteItem key={item.id} textValue={item.name}>
                    <Flex alignItems="center" minHeight={36} px={8}>
                        {item.name}
                    </Flex>
                </AutocompleteItem>
            )}
        </Autocomplete>
    </Flex>
);

Unstyled.args = {
    label: "Favorite Animal",
    placeholder: "Select an animal"
};

type Character = {
    id: number;
    image: string;
    name: string;
    status: "Alive" | "Dead" | "unknown";
};

/**
 * This example showcases a possible, real world product use-case. [It uses a real API](https://rickandmortyapi.com/).
 *
 * This example is useful for seeing how to deal with asynchronous lists, how to infinitely load the data, and how to customize the options to look as intended.
 */

export const RealWorldAsyncExample: StoryFn = () => {
    const list = useAsyncList<Character>({
        load: async ({ cursor, filterText, signal }) => {
            const response = await fetch(cursor || `https://rickandmortyapi.com/api/character?name=${filterText}`, {
                signal
            });
            const json = await response.json();

            return { cursor: json.info.next, items: json.results };
        }
    });

    return (
        <Flex column maxWidth={300}>
            <Autocomplete
                description={
                    <>
                        Find out more at <a href="https://rickandmortyapi.com/">rickandmortyapi.com</a>
                    </>
                }
                icon={<SearchIcon />}
                inputValue={list.filterText}
                items={list.items}
                label="Rick & Morty Character Search"
                loadingState={list.loadingState}
                menuTrigger="focus"
                onInputChange={list.setFilterText}
                onSelectionChange={action("selected")}
                onLoadMore={list.loadMore}
                placeholder="Try with Rick ..."
            >
                {item => (
                    <AutocompleteItem key={item.id} textValue={item.name}>
                        <Flex alignItems="center" gap={8} justifyContent="space-between" p={8}>
                            <Flex as="img" column borderRadius="100%" height={28} src={item.image} width={28} />
                            <Typography variant="label-small-regular">{item.name}</Typography>
                            <Flex
                                backgroundColor={
                                    item.status === "Alive"
                                        ? "green-300"
                                        : item.status === "Dead"
                                        ? "error-300"
                                        : "surface-300"
                                }
                                borderRadius="100%"
                                column
                                height={4}
                                width={4}
                            />
                        </Flex>
                    </AutocompleteItem>
                )}
            </Autocomplete>
        </Flex>
    );
};

export const CustomPortalContainer: StoryFn = () => {
    const [scrollingContainer, setScrollingContainer] = useState<HTMLDivElement | null>(null);

    return (
        <Flex
            ref={setScrollingContainer}
            backgroundColor="surface-50"
            column
            maxHeight={150}
            style={{ overflowY: "scroll" }}
        >
            <Flex alignItems="center" column justifyContent="center" minHeight={450}>
                <Flex column maxWidth={300}>
                    <Autocomplete
                        items={items}
                        menuTrigger="focus"
                        label="Favorite Animal"
                        placeholder="Select an animal"
                        scrollingContainer={scrollingContainer ?? undefined}
                    >
                        {item => (
                            <AutocompleteItem key={item.id} textValue={item.name}>
                                <Flex alignItems="center" minHeight={36} px={8}>
                                    {item.name}
                                </Flex>
                            </AutocompleteItem>
                        )}
                    </Autocomplete>
                </Flex>
            </Flex>
        </Flex>
    );
};
